import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ApiIcon from "@mui/icons-material/Api";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TimelineIcon from "@mui/icons-material/Timeline";
import PageHeader from "../../Components/UI/PageHeader";
import SectionCard from "../../Components/UI/SectionCard";
import {
  AUTH_INFO,
  BANK_APIS,
  BANK_DASHBOARD,
  DEFAULT_PAYMENT_POLICY,
  DEV_SIMULATOR,
  INTEGRATION_FLOW,
  NOT_BANK_APIS,
  PAYMENT_SERVICE_IDS,
  SERVICE_IDS,
  SETUP_CONFIG,
} from "./bankIntegrationData";

const mono = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: 13,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const JsonBlock = ({ data, label }) => {
  const text = data ? JSON.stringify(data, null, 2) : "— (no body)";
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy JSON"}>
          <IconButton size="small" onClick={copy}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: "#0f172a",
          color: "#e2e8f0",
          borderRadius: 2,
          ...mono,
        }}
      >
        {text}
      </Paper>
    </Box>
  );
};

const MethodChip = ({ method }) => {
  const colors = {
    GET: "info",
    POST: "success",
    PUT: "warning",
    DELETE: "error",
  };
  return (
    <Chip
      label={method}
      size="small"
      color={colors[method] || "default"}
      sx={{ fontWeight: 800, minWidth: 56 }}
    />
  );
};

const BankIntegration = () => {
  const [expandedApi, setExpandedApi] = useState("verify-user");

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <PageHeader
        title="Bank Integration Guide"
        subtitle="A–Z SEC@R partner bank APIs, payloads, payment-window behavior, and end-to-end process."
        actions={[
          { label: "5 bank APIs", key: "count", color: "primary", variant: "outlined" },
          { label: "Services OFF by default", key: "default", color: "warning", variant: "outlined" },
          { label: "Base: /api/secar", key: "base", variant: "outlined" },
        ]}
      />

      <SectionCard
        title="Authentication"
        subtitle="Required on every bank-facing endpoint"
        right={<Chip label={`Header: ${AUTH_INFO.headerName}`} size="small" color="warning" />}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {AUTH_INFO.description}
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#f8fafc", ...mono }}>
          {AUTH_INFO.example}
        </Paper>
        <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
          {AUTH_INFO.failure}
        </Typography>
      </SectionCard>

      <SectionCard
        title={DEFAULT_PAYMENT_POLICY.title}
        subtitle="How SEC@R customers differ from ordinary bank customers"
      >
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {DEFAULT_PAYMENT_POLICY.points.map((point) => (
            <Typography component="li" variant="body2" key={point} sx={{ mb: 0.75 }}>
              {point}
            </Typography>
          ))}
        </Box>
      </SectionCard>

      <SectionCard
        title={DEV_SIMULATOR.title}
        subtitle="Ports used in the Infinicue NEW-SECAR dev stack"
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Port</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DEV_SIMULATOR.rows.map((row) => (
                <TableRow key={row.service}>
                  <TableCell>{row.service}</TableCell>
                  <TableCell sx={mono}>{row.port}</TableCell>
                  <TableCell>{row.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          {DEV_SIMULATOR.adbReverse}
        </Typography>
      </SectionCard>

      <SectionCard
        title="Setup — what the bank provides to Infinicue"
        subtitle="SEC@R backend environment variables per partner bank"
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Variable</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Example</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SETUP_CONFIG.rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell sx={mono}>{row.key}</TableCell>
                  <TableCell sx={mono}>{row.example}</TableCell>
                  <TableCell>{row.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Bank API base path: <Box component="span" sx={mono}>{SETUP_CONFIG.bankBasePath}</Box>
        </Typography>
      </SectionCard>

      <SectionCard
        title="Service IDs"
        subtitle="Valid service identifiers for verify-user flags and enable/disable payloads"
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Payment window</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SERVICE_IDS.map((s) => (
                <TableRow key={s.id}>
                  <TableCell sx={mono}>{s.id}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>
                    {s.payment ? (
                      <Chip label="Yes" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Info only" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          Payment services sent on enable/disable: {PAYMENT_SERVICE_IDS.join(", ")}
        </Typography>
      </SectionCard>

      <SectionCard
        title={BANK_DASHBOARD.title}
        subtitle={BANK_DASHBOARD.description}
      >
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Path</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Auth</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {BANK_DASHBOARD.endpoints.map((ep) => (
                <TableRow key={ep.path}>
                  <TableCell>
                    <MethodChip method={ep.method} />
                  </TableCell>
                  <TableCell sx={mono}>{ep.path}</TableCell>
                  <TableCell variant="body2">{ep.auth}</TableCell>
                  <TableCell variant="body2">{ep.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="subtitle2" fontWeight={800} gutterBottom>
          Dashboard rules
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {BANK_DASHBOARD.uiRules.map((rule) => (
            <Typography component="li" variant="body2" key={rule} sx={{ mb: 0.5 }}>
              {rule}
            </Typography>
          ))}
        </Box>
      </SectionCard>

      <SectionCard
        title="End-to-end process (A → Z)"
        subtitle="From signup through payment window, netbanking login, geo check, and auto-disable"
        right={<TimelineIcon color="primary" />}
      >
        <Stepper orientation="vertical" nonLinear>
          {INTEGRATION_FLOW.map((item) => (
            <Step key={item.step} active expanded>
              <StepLabel
                optional={
                  item.bankApi ? (
                    <Chip
                      icon={<ApiIcon />}
                      label={item.bankApi}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Chip label="No bank call" size="small" sx={{ mt: 0.5 }} />
                  )
                }
              >
                <Typography fontWeight={800}>
                  {item.step}. {item.phase}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary">
                  <strong>Flow:</strong> {item.actor}
                </Typography>
                {item.secarEndpoint ? (
                  <Typography variant="body2" sx={{ mt: 0.5, ...mono }}>
                    SEC@R: {item.secarEndpoint}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {item.description}
                </Typography>
                {item.notes ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Note: {item.notes}
                  </Typography>
                ) : null}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </SectionCard>

      <SectionCard
        title="Bank APIs — full reference with payloads"
        subtitle="All endpoints partner banks must implement"
        right={<AccountBalanceIcon color="primary" />}
      >
        {BANK_APIS.map((api) => (
          <Accordion
            key={api.id}
            expanded={expandedApi === api.id}
            onChange={(_, open) => setExpandedApi(open ? api.id : false)}
            sx={{ mb: 1, "&:before": { display: "none" }, borderRadius: "12px !important" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <MethodChip method={api.method} />
                <Typography sx={mono} fontWeight={700}>
                  {api.path}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  — {api.title}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                When SEC@R calls this
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                {api.whenCalled.map((w) => (
                  <Chip key={w} label={w} size="small" variant="outlined" />
                ))}
              </Stack>

              {api.matchingRules?.length ? (
                <>
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    Implementation rules
                  </Typography>
                  <Box component="ul" sx={{ mt: 0, pl: 2.5, mb: 2 }}>
                    {api.matchingRules.map((rule) => (
                      <Typography component="li" variant="body2" key={rule} sx={{ mb: 0.5 }}>
                        {rule}
                      </Typography>
                    ))}
                  </Box>
                </>
              ) : null}

              <Divider sx={{ my: 2 }} />

              <JsonBlock data={api.request} label="Request payload (JSON body)" />
              <JsonBlock data={api.responseFound} label="Response — success / customer found" />
              {api.responseNotFound ? (
                <JsonBlock data={api.responseNotFound} label="Response — customer not found" />
              ) : null}

              {api.errors?.length ? (
                <>
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    Error responses
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>HTTP</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Detail</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {api.errors.map((e) => (
                          <TableRow key={`${e.code}-${e.detail}`}>
                            <TableCell>{e.code}</TableCell>
                            <TableCell>{e.detail}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
            </AccordionDetails>
          </Accordion>
        ))}
      </SectionCard>

      <SectionCard
        title="Not part of SEC@R bank integration"
        subtitle="These exist on bank simulators but use customer JWT — banks do not need them for SEC@R"
      >
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {NOT_BANK_APIS.map((path) => (
            <Chip key={path} label={path} size="small" variant="outlined" sx={mono} />
          ))}
        </Stack>
      </SectionCard>
    </Box>
  );
};

export default BankIntegration;
